import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { toastAdd } from "@/mk2/actions";
import LeftMenu from "@/mk2/apps/blogs/components/LeftMenu";
import forumCategoriesPageReducer, {
    ForumCategoriesPageState,
} from "@/mk2/apps/forum/containers/ForumCategories/ForumCategories.reducers";
import forumCategoriesSaga from "@/mk2/apps/forum/containers/ForumCategories/ForumCategories.sagas";
import { getForumCategoriesPageState } from "@/mk2/apps/forum/containers/ForumCategories/ForumCategories.selectors";
import { CategoryEntity, CategorySchema } from "@/mk2/apps/forum/schemas";
import { forumListOfForumsUrl } from "@/mk2/apps/forum/urls";
import { groupSharedJoinGroup } from "@/mk2/apps/groups/containers/GroupShared/GroupShared.actions";
import groupSharedReducer from "@/mk2/apps/groups/containers/GroupShared/GroupShared.reducers";
import groupSharedSaga from "@/mk2/apps/groups/containers/GroupShared/GroupShared.sagas";
import { getGroupSharedState } from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import {
    groupsListFromCategoryUrl,
    groupsListUrl,
    groupsNewGroupUrl,
} from "@/mk2/apps/groups/urls";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { Toast } from "@/mk2/components/toasts/Toast";
import { AppName } from "@/mk2/containers/Application/Application";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { GroupEntity, GroupSchema, MenuSelection, UserEntity } from "@/mk2/schemas";
import { findDenormalizedEntityBySlug, getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    GROUPS_CREATE_NEW_GROUP,
    GROUP_LIST,
    LIST_OF_FORUMS,
    SELECT_CATEGORY,
    SEO_TITLE_GROUP_LIST,
    SEO_TITLE_SELECT_CATEGORY,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import { ListOfGroups } from "./ListOfGroups";
import {
    listOfGroupsFetchNew,
    listOfGroupsLoad,
    listOfGroupsSetSelectingCategory,
} from "./ListOfGroups.actions";
import reducer, { GroupsSearchState, ListOfGroupsPageState } from "./ListOfGroups.reducers";
import saga from "./ListOfGroups.sagas";
import { getListOfGroupsPageState, getListOfGroupsSearchState } from "./ListOfGroups.selectors";

interface OwnProps {}

interface StateProps {
    after: number;
    askedForMembership: number[];
    categories: CategoryEntity[];
    categoriesLoadingState: LoadingState;
    category: CategoryEntity;
    count: number;
    errorMessage: string;
    groups: GroupEntity[];
    hasNext: number;
    hasPrevious: number;
    isMobile: boolean;
    isSelectingCategory: boolean;
    joining: number[];
    loadingState: LoadingState;
    memberships: number[];
    newGroups: GroupEntity[];
    numGroupsInCategory: { [categoryId: number]: number };
    query: string;
    requestUser: UserEntity;
    requestUserPermissions: string[];
}

interface DispatchProps {
    onCategorySelectClose();
    onCategorySelectOpen();
    onErrorToast(msg: string);
    onFetchNew(limit: number, after: number);
    onJoinGroup(groupId: number, origin: string);
    onLoad(category: string, query: string, limitNew: number, after?: number);
}

type RouteProps = RouteComponentProps<{
    categorySlug?: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export class ListOfGroupsPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: { params },
                location: { search },
            } = ownProps;
            const qs = parse(search, { parseNumbers: true }) || {};
            const query = (qs.query as string) || "";

            const fcState: ForumCategoriesPageState = getForumCategoriesPageState(state);
            const groupSharedState = getGroupSharedState(state);
            const pageState: ListOfGroupsPageState = getListOfGroupsPageState(state);
            const searchState: GroupsSearchState = getListOfGroupsSearchState(
                state,
                params.categorySlug,
                query,
            );

            return {
                after: qs.after as number,
                askedForMembership: groupSharedState.askedForMembership,
                categories: getDenormalizedEntities<CategoryEntity>(
                    state,
                    CategorySchema,
                    fcState.categoryIds,
                ),
                categoriesLoadingState: fcState.loadingState,
                category: findDenormalizedEntityBySlug<CategoryEntity>(
                    state,
                    CategorySchema,
                    params.categorySlug,
                ),
                count: searchState.count,
                errorMessage: searchState.errorMessage,
                groups: getDenormalizedEntities<GroupEntity>(state, GroupSchema, searchState.ids),
                hasNext: searchState.hasNext,
                hasPrevious: searchState.hasPrevious,
                isMobile: getRequestDeviceMobile(state),
                isSelectingCategory: pageState.selectingCategory,
                joining: groupSharedState.joining,
                loadingState: searchState.loadingState,
                memberships: groupSharedState.memberships,
                newGroups: getDenormalizedEntities<GroupEntity>(
                    state,
                    GroupSchema,
                    pageState.newGroupsIds,
                ),
                numGroupsInCategory: fcState.numGroupsInCategory,
                query,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onCategorySelectClose: () => listOfGroupsSetSelectingCategory(false),
            onCategorySelectOpen: () => listOfGroupsSetSelectingCategory(true),
            onErrorToast: (msg: string) =>
                toastAdd("error", {
                    component: ({ remove }) => (
                        <Toast type="error" remove={remove}>
                            {msg}
                        </Toast>
                    ),
                }),
            onFetchNew: listOfGroupsFetchNew,
            onJoinGroup: groupSharedJoinGroup,
            onLoad: listOfGroupsLoad,
        };
    }

    public getMetaTitle(props: Props): string {
        return props.isSelectingCategory ? SEO_TITLE_SELECT_CATEGORY : SEO_TITLE_GROUP_LIST;
    }

    public render(props: Props): false | JSX.Element {
        return <ListOfGroups {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        const { location } = props;
        return <LeftMenu location={location} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Groups Feed - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: "Groups - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Groups - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, groups } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!groups || groups.length < 20) {
            return null;
        }

        return {
            zone: "Groups - Anchor Ad Leaderboard (Mobile)",
        };
    }

    public hasHeaderBackButton(props: Props): boolean {
        return props.isSelectingCategory;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        const {
            match: {
                params: { categorySlug },
            },
            isSelectingCategory,
        } = props;

        if (isSelectingCategory) {
            return null;
        }

        return [
            <Btn
                key="forums"
                type={BtnType.Tab}
                label={LIST_OF_FORUMS}
                link={forumListOfForumsUrl}
            />,
            <Btn
                key="groups"
                type={BtnType.Tab}
                label={GROUP_LIST}
                link={{
                    pathname: categorySlug
                        ? url(groupsListFromCategoryUrl, { categorySlug })
                        : groupsListUrl,
                    search: props.location.search,
                }}
                active
            />,
        ];
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        if (props.isSelectingCategory) {
            return null;
        }

        return <Btn type={BtnType.Blue} label={GROUPS_CREATE_NEW_GROUP} link={groupsNewGroupUrl} />;
    }

    public getHeaderTitle(props: Props): string {
        if (props.isSelectingCategory) {
            return SELECT_CATEGORY;
        }

        return null;
    }

    public getHeaderLeft(props: Props): JSX.Element {
        if (props.isSelectingCategory) {
            return (
                <Btn
                    icon="mkicon-arrow-back@3x.png"
                    layout={BtnLayout.Icon}
                    onClick={props.onCategorySelectClose}
                />
            );
        }

        return super.getHeaderLeft(props);
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "groups",
            activeItem: "list",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.groups.listOfGroups", reducer);
        store.injectSaga("containers.groups.listOfGroups", saga);

        store.injectReducer("containers.groups.groupShared", groupSharedReducer);
        store.injectSaga("containers.groups.groupShared", groupSharedSaga);

        store.injectReducer("containers.forum.forumCategories", forumCategoriesPageReducer);
        store.injectSaga("containers.forum.forumCategories", forumCategoriesSaga);
    }
}

export default ListOfGroupsPage;
